// Panel listing the focal issue's links: GitLab issue-links (authoritative)
// + #nnn body references (informal). Two sections; the body-ref section
// only renders when there are refs that didn't dedupe against a link.

import type { JSX } from "react";

import type {
  IssueLinkEdgeType,
  IssueLinkRow,
} from "@loom/shared/dashboard";

import { useT, type MessageKey, type Translator } from "../../i18n/index.js";
import { STAGE_LABEL_KEY, STAGE_VAR } from "../stage";

const EDGE_LABEL_KEY: Record<IssueLinkEdgeType, MessageKey> = {
  relates_to: "dashboard.links.edge.relates_to",
  blocks: "dashboard.links.edge.blocks",
  is_blocked_by: "dashboard.links.edge.is_blocked_by",
};

export interface IssueLinksPanelProps {
  readonly links: ReadonlyArray<IssueLinkRow>;
  readonly projectId: number;
  readonly onNavigate: (iid: number) => void;
  /**
   * Split-epic child iids (server-parsed from the `## Sub-issues` task-list).
   * Links in this set render FIRST under their own "Sub-issues" group with a
   * shipped-progress line — the durable, GitLab-derived epic view on return
   * visits (the session-only BreakdownCard never renders here).
   */
  readonly childIids?: ReadonlyArray<number>;
}

const LinkRow = ({
  link,
  onNavigate,
  t,
}: {
  readonly link: IssueLinkRow;
  readonly onNavigate: (iid: number) => void;
  readonly t: Translator;
}): JSX.Element => (
  <button
    type="button"
    className={`issue-link-row state-${link.state}`}
    onClick={() => onNavigate(link.iid)}
  >
    <span
      className="issue-link-stage-dot"
      style={{ background: STAGE_VAR[link.stage] }}
      title={t(STAGE_LABEL_KEY[link.stage])}
    />
    {link.edgeType && (
      <span className={`issue-link-edge edge-${link.edgeType}`}>
        {t(EDGE_LABEL_KEY[link.edgeType])}
      </span>
    )}
    <span className="issue-link-key">#{link.iid}</span>
    <span className="issue-link-title">{link.title}</span>
    <span className="issue-link-stage">{t(STAGE_LABEL_KEY[link.stage])}</span>
  </button>
);

export const IssueLinksPanel = ({
  links,
  onNavigate,
  childIids,
}: IssueLinksPanelProps): JSX.Element => {
  const t = useT();
  const childSet = new Set(childIids ?? []);
  const children = links.filter((l) => childSet.has(l.iid));
  const formal = links.filter(
    (l) => l.source === "gitlab_link" && !childSet.has(l.iid),
  );
  const refs = links.filter((l) => l.source === "ref" && !childSet.has(l.iid));
  const shippedChildren = children.filter((l) => l.stage === "shipped").length;

  if (links.length === 0) {
    return (
      <section className="issue-links-panel">
        <h3 className="issue-panel-h">{t("dashboard.links.title")}</h3>
        <p className="issue-panel-empty">
          {t("dashboard.links.empty")}
        </p>
      </section>
    );
  }

  return (
    <section className="issue-links-panel">
      <h3 className="issue-panel-h">{t("dashboard.links.title")}</h3>
      {children.length > 0 && (
        <div className="issue-link-group">
          <div className="issue-link-group-label">
            {t("breakdown.subIssuesGroup")}
            <span className="issue-link-group-progress">
              {" · "}
              {t("breakdown.subIssuesProgress", {
                done: String(shippedChildren),
                total: String(children.length),
              })}
            </span>
          </div>
          {children.map((l) => (
            <LinkRow key={`ch-${l.iid}`} link={l} onNavigate={onNavigate} t={t} />
          ))}
        </div>
      )}
      {formal.length > 0 && (
        <div className="issue-link-group">
          <div className="issue-link-group-label">{t("dashboard.links.gitlabLinks")}</div>
          {formal.map((l) => (
            <LinkRow key={`gl-${l.iid}`} link={l} onNavigate={onNavigate} t={t} />
          ))}
        </div>
      )}
      {refs.length > 0 && (
        <div className="issue-link-group">
          <div
            className="issue-link-group-label"
            title={t("dashboard.links.referencedTitle")}
          >
            {t("dashboard.links.referencedInBody")}
          </div>
          {refs.map((l) => (
            <LinkRow key={`rf-${l.iid}`} link={l} onNavigate={onNavigate} t={t} />
          ))}
        </div>
      )}
    </section>
  );
};
